import React, { useState, useMemo } from "react";
import { useCostData } from "../stores/appStore";
import { useEffect } from "react";
import { OPERATING_COSTS_CATALOG, type OperatingCostTemplate, searchCosts, getCostsByCategory } from "../data/operatingCostsCatalog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Search, Plus, Filter, X, CheckCircle, Info, Download, Upload, Calendar, DollarSign, Edit } from "lucide-react";

interface OperatingCostsManagerProps {
  planningMonths: number;
}

interface OperatingCostsTableProps {
  operatingCosts: any[];
  planningMonths: number;
  onUpdateCost: (costId: string, monthIndex: number, value: number) => void;
  onRemoveCost: (costId: string) => void;
  onEditCost: (cost: any) => void;
}

function OperatingCostsTable({
  operatingCosts,
  planningMonths,
  onUpdateCost,
  onRemoveCost,
  onEditCost,
}: OperatingCostsTableProps) {
  // Function to check if cost is within typical range
  const getCostValidation = (cost: any, monthIndex: number) => {
    if (!cost.typicalRange) return null;

    const monthlyCost = cost.monthlyCosts[monthIndex];
    if (monthlyCost === 0) return null;

    const { min, max } = cost.typicalRange;
    if (monthlyCost < min) return { status: 'low', message: `Below typical range (₹${min.toLocaleString()})` };
    if (monthlyCost > max) return { status: 'high', message: `Above typical range (₹${max.toLocaleString()})` };
    return { status: 'good', message: 'Within typical range' };
  };

  // Function to get color class based on cost value
  const getCostColorClass = (value: number, maxValue: number = 100000) => {
    if (value === 0) return 'color-info-bg text-gray-500 dark:text-gray-400';
    const intensity = value / maxValue;
    if (intensity < 0.3) return 'color-success-bg';
    if (intensity < 0.7) return 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-300';
    return 'color-danger-bg';
  };

  // Function to get category badge color
  const getCategoryBadgeColor = (category: string) => {
    const colors: Record<string, string> = {
      infrastructure: 'badge-infrastructure',
      facilities: 'badge-facilities',
      tools: 'badge-tools',
      marketing: 'badge-marketing',
      sales: 'badge-sales',
      legal: 'badge-legal',
      insurance: 'badge-insurance',
      'professional-services': 'badge-professional-services',
      other: 'badge-other',
    };
    return colors[category] || 'badge-other';
  };

  // Function to get quarter total for a cost
  const getQuarterTotal = (cost: any, quarterIndex: number) => {
    const startMonth = quarterIndex * 3;
    const endMonth = Math.min(startMonth + 3, planningMonths);
    return cost.monthlyCosts.slice(startMonth, endMonth).reduce((sum: number, cost: number) => sum + cost, 0);
  };

  // Function to set all months in a quarter to a specific value
  const setQuarterCost = (costId: string, quarterIndex: number, value: number) => {
    const startMonth = quarterIndex * 3;
    const endMonth = Math.min(startMonth + 3, planningMonths);

    for (let monthIndex = startMonth; monthIndex < endMonth; monthIndex++) {
      onUpdateCost(costId, monthIndex, value);
    }
  };

  return (
    <div className="space-y-4">
      {/* Usage Guide & Color Legend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-[10px] font-bold">i</span>
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-100">Planning Guide</h4>
                <div className="text-[10px] text-blue-800 dark:text-blue-200 space-y-0.5">
                  <p>• <strong>Quarter buttons (+)</strong> set all months to suggested amount</p>
                  <p>• <strong>Arrow keys</strong> navigate between cells</p>
                  <p>• <strong>Compact design</strong> for detailed cost planning</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Color Legend */}
        <Card className="border-gray-200 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-950/20">
          <CardContent className="p-3">
            <div className="space-y-1">
              <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">Cost Indicators</h4>
              <div className="grid grid-cols-2 gap-1">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-gray-50 border-gray-200 dark:bg-gray-900 dark:border-gray-700"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">Empty (₹0)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">Low</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">Medium</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">High</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px]" style={{
            backgroundImage: `
              linear-gradient(to right, transparent 0%, transparent calc(25% - 2px), rgba(59, 130, 246, 0.1) calc(25% - 2px), rgba(59, 130, 246, 0.1) calc(25% + 2px), transparent calc(25% + 2px)),
              linear-gradient(to right, transparent 0%, transparent calc(50% - 2px), rgba(59, 130, 246, 0.1) calc(50% - 2px), rgba(59, 130, 246, 0.1) calc(50% + 2px), transparent calc(50% + 2px)),
              linear-gradient(to right, transparent 0%, transparent calc(75% - 2px), rgba(59, 130, 246, 0.1) calc(75% - 2px), rgba(59, 130, 246, 0.1) calc(75% + 2px), transparent calc(75% + 2px))
            `,
            backgroundSize: '100% 100%'
          }}>
            {/* Sticky Header with Quarter Groups */}
            <thead className="bg-muted/70 sticky top-0 z-10">
              {/* Quarter Group Headers */}
              <tr className="border-b-2 border-primary/20">
                <th colSpan={4} className="px-3 py-1.5 bg-muted/50 border-r-2 border-primary/30"></th>
                {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => (
                  <th
                    key={`quarter-${quarterIndex}`}
                    colSpan={4} // 3 months + 1 total column
                    className="px-2 py-2 text-center bg-primary/5 border-r-2 border-primary/30 last:border-r-0"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-primary">Q{quarterIndex + 1}</span>
                      </div>
                      <div className="text-[10px] font-semibold text-primary">
                        M{quarterIndex * 3 + 1}-{Math.min((quarterIndex + 1) * 3, planningMonths)}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
              {/* Column Headers */}
              <tr className="border-b border-border/50">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50">
                  Cost Item
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50">
                  Category
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50">
                  Frequency
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50 w-16">
                  Actions
                </th>
                {/* Month columns and quarter totals */}
                {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => {
                  const months = [1, 2, 3].map(m => quarterIndex * 3 + m).filter(m => m <= planningMonths);
                  return (
                    <React.Fragment key={`quarter-group-${quarterIndex}`}>
                      {/* Month columns */}
                      {months.map((month) => (
                        <th
                          key={`month-${month - 1}`}
                          className="px-2 py-2 text-center text-[10px] font-semibold min-w-[60px] border-r border-border/30 bg-muted/50"
                        >
                          <div className="text-muted-foreground font-medium">
                            {month}
                          </div>
                        </th>
                      ))}
                      {/* Quarter Total Column */}
                      <th className="px-2 py-2 text-center text-[10px] font-semibold min-w-[65px] border-r-2 border-primary/30 bg-primary/10">
                        <div className="space-y-0.5">
                          <div className="text-primary font-bold text-[9px]">Q{quarterIndex + 1}</div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-3 w-3 p-0 text-primary hover:bg-primary/20"
                            onClick={() => {
                              // Set all costs in this quarter to suggested amount
                              operatingCosts.forEach(cost => {
                                const suggestedCost = cost.typicalRange ?
                                  Math.round((cost.typicalRange.min + cost.typicalRange.max) / 2) : 10000;
                                setQuarterCost(cost.id, quarterIndex, suggestedCost);
                              });
                            }}
                            title={`Set all Q${quarterIndex + 1} costs to suggested amounts`}
                          >
                            <span className="text-[8px] font-bold">+</span>
                          </Button>
                        </div>
                      </th>
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/20">
              {operatingCosts.map((cost, costIndex) => (
                <tr key={cost.id} className={`hover:bg-muted/30 transition-all duration-150 ${
                  costIndex % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                }`}>
                  {/* Cost Name */}
                  <td className="px-3 py-2 border-r border-border/30">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <DollarSign className="w-3 h-3 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground text-sm">{cost.name}</div>
                        {cost.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">{cost.description}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-3 py-2 border-r border-border/30">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${getCategoryBadgeColor(cost.category)}`}>
                      {cost.category.replace('-', ' ')}
                    </span>
                  </td>

                  {/* Frequency */}
                  <td className="px-3 py-2 border-r border-border/30">
                    <div className="text-xs font-medium text-muted-foreground capitalize">
                      {cost.frequency}
                    </div>
                  </td>

                   {/* Actions */}
                   <td className="px-2 py-2 border-r border-border/30">
                     <div className="flex items-center justify-center gap-0.5">
                       {cost.isCustom && (
                         <Button
                           variant="ghost"
                           size="sm"
                           onClick={() => onEditCost(cost)}
                           className="h-6 w-6 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                           title="Edit cost"
                         >
                           <Edit className="w-3 h-3" />
                         </Button>
                       )}
                       <Button
                         variant="ghost"
                         size="sm"
                         onClick={() => onRemoveCost(cost.id)}
                         className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                         title="Remove cost"
                       >
                         <X className="w-3 h-3" />
                       </Button>
                     </div>
                   </td>

                  {/* Cost inputs for each month and quarter totals */}
                  {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => {
                    const months = [1, 2, 3].map(m => quarterIndex * 3 + m).filter(m => m <= planningMonths);
                    return (
                      <React.Fragment key={`quarter-group-${quarterIndex}`}>
                        {/* Month columns */}
                        {months.map((month) => {
                          const monthIndex = month - 1;
                          const monthlyCost = cost.monthlyCosts[monthIndex] || 0;
                          const validation = getCostValidation(cost, monthIndex);
                          const colorClass = getCostColorClass(monthlyCost);
                          return (
                            <td
                              key={`month-${monthIndex}`}
                              className="px-2 py-2 text-center border-r border-border/30 bg-muted/20"
                            >
                              <div className="relative">
                                <Input
                                  type="number"
                                  min="0"
                                  value={monthlyCost || ''}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    onUpdateCost(cost.id, monthIndex, Math.max(0, value));
                                  }}
                                  onKeyDown={(e) => {
                                    // Allow tab navigation and common keyboard shortcuts
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      const input = e.target as HTMLInputElement;
                                      input.select();
                                    }
                                    // Arrow key navigation between cells
                                    if (e.key === 'ArrowRight' && !e.shiftKey) {
                                      e.preventDefault();
                                      const nextCell = e.currentTarget.closest('td')?.nextElementSibling?.querySelector('input');
                                      nextCell?.focus();
                                    }
                                    if (e.key === 'ArrowLeft' && !e.shiftKey) {
                                      e.preventDefault();
                                      const prevCell = e.currentTarget.closest('td')?.previousElementSibling?.querySelector('input');
                                      prevCell?.focus();
                                    }
                                  }}
                                  className={`w-12 h-6 text-center text-xs font-medium border rounded transition-all px-0.5 ${colorClass} hover:shadow-sm focus:shadow-md focus:ring-1 ${
                                    validation?.status === 'low' ? 'border-yellow-500' :
                                    validation?.status === 'high' ? 'border-red-500' : ''
                                  }`}
                                  placeholder="0"
                                  title={`Month ${month} cost for ${cost.name}`}
                                  aria-label={`${cost.name} - Month ${month} cost`}
                                />
                              </div>
                            </td>
                          );
                        })}
                        {/* Quarter Total Column */}
                        <td className="px-2 py-2 text-center border-r-2 border-primary/30 bg-primary/5">
                          <div className="flex flex-col items-center gap-0.5">
                            <div className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCostColorClass(getQuarterTotal(cost, quarterIndex))}`}>
                              ₹{(getQuarterTotal(cost, quarterIndex) / 1000).toFixed(0)}k
                            </div>
                          </div>
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function OperatingCostsManager({ planningMonths }: OperatingCostsManagerProps) {
  const { operatingCosts, setOperatingCosts } = useCostData();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCost, setEditingCost] = useState<any>(null);
  const [showMonthSelectionModal, setShowMonthSelectionModal] = useState(false);
  const [selectedTemplateForMonths, setSelectedTemplateForMonths] = useState<OperatingCostTemplate | null>(null);
  const [monthSelections, setMonthSelections] = useState<boolean[]>([]);
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [resourceCount, setResourceCount] = useState<number>(1);
  const [customCostForm, setCustomCostForm] = useState({
    name: "",
    description: "",
    category: "other" as OperatingCostTemplate['category'],
    frequency: "monthly" as OperatingCostTemplate['frequency'],
    monthlyCost: 0,
    isOneTime: false,
    oneTimeMonth: 1,
  });

  // Auto-save operating costs to startup when they change
  useEffect(() => {
    if (operatingCosts.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const startupId = urlParams.get('startupId');

      if (startupId) {
        const savedStartups = localStorage.getItem('userStartups');
        if (savedStartups) {
          try {
            const startups = JSON.parse(savedStartups);
            const startupIndex = startups.findIndex((s: any) => s.id === startupId);

            if (startupIndex >= 0) {
              // Update startup with current operating costs
              startups[startupIndex] = {
                ...startups[startupIndex],
                updatedAt: new Date(),
                data: {
                  ...startups[startupIndex].data,
                  operatingCosts: operatingCosts
                }
              };
              localStorage.setItem('userStartups', JSON.stringify(startups));
            }
          } catch (error) {
            console.error('Error auto-saving operating costs:', error);
          }
        }
      }
    }
  }, [operatingCosts]);

  // Filter catalog based on search and category
  const filteredCatalog = useMemo(() => {
    let results = OPERATING_COSTS_CATALOG;

    if (selectedCategory !== "all") {
      results = getCostsByCategory(selectedCategory as OperatingCostTemplate['category']);
    }

    if (searchQuery.trim()) {
      results = searchCosts(searchQuery);
    }

    return results;
  }, [searchQuery, selectedCategory]);

  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = Array.from(new Set(OPERATING_COSTS_CATALOG.map(cost => cost.category)));
    return cats.sort();
  }, []);

  // Add cost from catalog - now shows month selection modal
  const addCostFromCatalog = (template: OperatingCostTemplate) => {
    setSelectedTemplateForMonths(template);
    setMonthSelections(Array(planningMonths).fill(false)); // Initialize all months as unselected
    const suggestedCost = template.typicalRange ?
      Math.round((template.typicalRange.min + template.typicalRange.max) / 2) : 0;
    setCustomAmount(suggestedCost);
    setResourceCount(1); // Default to 1 resource
    setShowMonthSelectionModal(true);
  };

  // Confirm adding cost with selected months
  const confirmAddCostWithMonths = () => {
    if (!selectedTemplateForMonths) return;

    const template = selectedTemplateForMonths;
    const finalAmount = customAmount * resourceCount;

    const newCost = {
      id: `${template.id}-${Date.now()}`,
      name: resourceCount > 1 ? `${template.name} (${resourceCount}x)` : template.name,
      description: template.description,
      monthlyCosts: Array(planningMonths).fill(0),
      category: template.category,
      frequency: template.frequency,
      typicalRange: template.typicalRange,
      isCustom: false,
      tags: template.tags,
    };

    // Set costs only for selected months using custom amount and resource count
    monthSelections.forEach((selected, monthIndex) => {
      if (selected) {
        newCost.monthlyCosts[monthIndex] = finalAmount;
      }
    });

    setOperatingCosts([...operatingCosts, newCost]);
    setShowMonthSelectionModal(false);
    setSelectedTemplateForMonths(null);
  };

  // Add custom cost
  const addCustomCost = () => {
    if (!customCostForm.name.trim()) return;

    const newCost = {
      id: `custom-${Date.now()}`,
      name: customCostForm.name,
      description: customCostForm.description,
      monthlyCosts: Array(planningMonths).fill(0),
      category: customCostForm.category,
      frequency: customCostForm.frequency,
      isCustom: true,
      tags: [],
    };

    // Set the cost based on form input
    if (customCostForm.isOneTime) {
      newCost.monthlyCosts[customCostForm.oneTimeMonth - 1] = customCostForm.monthlyCost;
    } else {
      newCost.monthlyCosts = Array(planningMonths).fill(customCostForm.monthlyCost);
    }

    setOperatingCosts([...operatingCosts, newCost]);

    // Reset form
    setCustomCostForm({
      name: "",
      description: "",
      category: "other",
      frequency: "monthly",
      monthlyCost: 0,
      isOneTime: false,
      oneTimeMonth: 1,
    });
    setShowAddDialog(false);
  };

  // Start editing a cost
  const startEditingCost = (cost: any) => {
    setEditingCost(cost);
    setCustomCostForm({
      name: cost.name,
      description: cost.description || "",
      category: cost.category,
      frequency: cost.frequency,
      monthlyCost: cost.monthlyCosts[0] || 0, // Use first month value for editing
      isOneTime: cost.frequency === 'one-time',
      oneTimeMonth: cost.frequency === 'one-time' ? (cost.monthlyCosts.findIndex((c: number) => c > 0) + 1) : 1,
    });
    setShowAddDialog(true);
  };

  // Edit custom cost
  const editCustomCost = () => {
    if (!customCostForm.name.trim() || !editingCost) return;

    const updatedCost = {
      ...editingCost,
      name: customCostForm.name,
      description: customCostForm.description,
      category: customCostForm.category,
      frequency: customCostForm.frequency,
    };

    // Update the cost amount based on form input
    if (customCostForm.isOneTime) {
      updatedCost.monthlyCosts = Array(planningMonths).fill(0);
      updatedCost.monthlyCosts[customCostForm.oneTimeMonth - 1] = customCostForm.monthlyCost;
    } else {
      updatedCost.monthlyCosts = Array(planningMonths).fill(customCostForm.monthlyCost);
    }

    setOperatingCosts(operatingCosts.map(cost =>
      cost.id === editingCost.id ? updatedCost : cost
    ));

    // Reset form and editing state
    setCustomCostForm({
      name: "",
      description: "",
      category: "other",
      frequency: "monthly",
      monthlyCost: 0,
      isOneTime: false,
      oneTimeMonth: 1,
    });
    setEditingCost(null);
    setShowAddDialog(false);
  };

  // Update cost for specific month
  const updateCost = (costId: string, monthIndex: number, value: number) => {
    setOperatingCosts(
      operatingCosts.map(cost =>
        cost.id === costId
          ? {
              ...cost,
              monthlyCosts: cost.monthlyCosts.map((monthlyCost, index) =>
                index === monthIndex ? value : monthlyCost
              )
            }
          : cost
      )
    );
  };

  // Remove cost
  const removeCost = (costId: string) => {
    setOperatingCosts(operatingCosts.filter(cost => cost.id !== costId));
  };

  // Calculate total cost for a cost item
  const calculateTotalCost = (cost: any) => {
    return cost.monthlyCosts.reduce((sum: number, monthlyCost: number) => sum + monthlyCost, 0);
  };

  // Check if cost is within typical range
  const getCostValidation = (cost: any, monthIndex: number) => {
    if (!cost.typicalRange) return null;

    const monthlyCost = cost.monthlyCosts[monthIndex];
    if (monthlyCost === 0) return null;

    const { min, max } = cost.typicalRange;
    if (monthlyCost < min) return { status: 'low', message: `Below typical range (₹${min.toLocaleString()})` };
    if (monthlyCost > max) return { status: 'high', message: `Above typical range (₹${max.toLocaleString()})` };
    return { status: 'good', message: 'Within typical range' };
  };

  // Export operating costs configuration
  const exportCosts = () => {
    const exportData = {
      operatingCosts,
      exportedAt: new Date().toISOString(),
      planningMonths,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operating-costs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import operating costs configuration
  const importCosts = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target?.result as string);
        if (importData.operatingCosts && Array.isArray(importData.operatingCosts)) {
          // Resize costs to match current planning months
          const resizedCosts = importData.operatingCosts.map((cost: any) => ({
            ...cost,
            monthlyCosts: cost.monthlyCosts.length < planningMonths
              ? [...cost.monthlyCosts, ...Array(planningMonths - cost.monthlyCosts.length).fill(0)]
              : cost.monthlyCosts.slice(0, planningMonths)
          }));

          setOperatingCosts(resizedCosts);
          alert(`Successfully imported ${resizedCosts.length} operating costs.`);
        } else {
          alert('Invalid file format. Please select a valid operating costs export file.');
        }
      } catch (error) {
        alert('Error importing file. Please check the file format.');
      }
    };
    reader.readAsText(file);
    // Reset the input
    event.target.value = '';
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="costs" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="costs">Your Costs ({operatingCosts.length})</TabsTrigger>
          <TabsTrigger value="catalog">Cost Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="catalog" className="space-y-6">
          {/* Search and Filter Controls */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Search className="w-5 h-5 text-muted-foreground" />
                Operating Costs Catalog
              </CardTitle>
              <CardDescription>
                Search and add standard operating costs from our comprehensive catalog
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Search costs (e.g., 'AWS', 'marketing', 'office rent')..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Dialog open={showAddDialog} onOpenChange={(open) => {
               setShowAddDialog(open);
               if (!open) {
                 setEditingCost(null);
                 setCustomCostForm({
                   name: "",
                   description: "",
                   category: "other",
                   frequency: "monthly",
                   monthlyCost: 0,
                   isOneTime: false,
                   oneTimeMonth: 1,
                 });
               }
             }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Custom Cost
                    </Button>
                  </DialogTrigger>
                   <DialogContent className="max-w-md">
                     <DialogHeader>
                       <DialogTitle>{editingCost ? 'Edit Operating Cost' : 'Add Custom Operating Cost'}</DialogTitle>
                     </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="name">Cost Name</Label>
                        <Input
                          id="name"
                          value={customCostForm.name}
                          onChange={(e) => setCustomCostForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Custom Software License"
                        />
                      </div>
                      <div>
                        <Label htmlFor="description">Description (Optional)</Label>
                        <Textarea
                          id="description"
                          value={customCostForm.description}
                          onChange={(e) => setCustomCostForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Brief description of this cost"
                          rows={2}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="category">Category</Label>
                          <Select
                            value={customCostForm.category}
                            onValueChange={(value) => setCustomCostForm(prev => ({ ...prev, category: value as any }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="infrastructure">Infrastructure</SelectItem>
                              <SelectItem value="facilities">Facilities</SelectItem>
                              <SelectItem value="tools">Tools</SelectItem>
                              <SelectItem value="marketing">Marketing</SelectItem>
                              <SelectItem value="sales">Sales</SelectItem>
                              <SelectItem value="legal">Legal</SelectItem>
                              <SelectItem value="insurance">Insurance</SelectItem>
                              <SelectItem value="professional-services">Professional Services</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="frequency">Frequency</Label>
                          <Select
                            value={customCostForm.frequency}
                            onValueChange={(value) => setCustomCostForm(prev => ({ ...prev, frequency: value as any }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="yearly">Yearly</SelectItem>
                              <SelectItem value="one-time">One-time</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="cost">Cost Amount (₹)</Label>
                        <Input
                          id="cost"
                          type="number"
                          value={customCostForm.monthlyCost}
                          onChange={(e) => setCustomCostForm(prev => ({ ...prev, monthlyCost: Number(e.target.value) }))}
                          placeholder="Enter the cost amount"
                          min="0"
                        />
                      </div>
                      {customCostForm.frequency === 'one-time' && (
                        <div>
                          <Label htmlFor="month">Month</Label>
                          <Select
                            value={customCostForm.oneTimeMonth.toString()}
                            onValueChange={(value) => setCustomCostForm(prev => ({ ...prev, oneTimeMonth: Number(value) }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: planningMonths }, (_, i) => (
                                <SelectItem key={i + 1} value={(i + 1).toString()}>
                                  Month {i + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                     <DialogFooter>
                       <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                         Cancel
                       </Button>
                       <Button onClick={editingCost ? editCustomCost : addCustomCost} disabled={!customCostForm.name.trim()}>
                         {editingCost ? 'Update Cost' : 'Add Cost'}
                       </Button>
                 </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Month Selection Modal */}
            <Dialog open={showMonthSelectionModal} onOpenChange={setShowMonthSelectionModal}>
              <DialogContent className="max-w-4xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Select Months for {selectedTemplateForMonths?.name}
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    Choose which months this cost should apply to. Selected months will use the typical cost amount.
                  </p>
                </DialogHeader>

                {selectedTemplateForMonths && (
                  <div className="space-y-4">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                       <div className="flex-1">
                         <h4 className="font-medium">{selectedTemplateForMonths.name}</h4>
                         <p className="text-sm text-muted-foreground">{selectedTemplateForMonths.description}</p>
                       </div>
                       <div className="space-y-3">
                         <div>
                           <Label htmlFor="custom-amount" className="text-sm font-medium">Cost Amount (₹)</Label>
                           <Input
                             id="custom-amount"
                             type="number"
                             value={customAmount}
                             onChange={(e) => setCustomAmount(Number(e.target.value))}
                             placeholder="Enter amount"
                             min="0"
                             className="mt-1"
                           />
                           {selectedTemplateForMonths.typicalRange && (
                             <p className="text-xs text-muted-foreground mt-1">
                               Suggested: ₹{((selectedTemplateForMonths.typicalRange.min + selectedTemplateForMonths.typicalRange.max) / 2).toLocaleString()}
                             </p>
                           )}
                         </div>
                         <div>
                           <Label htmlFor="resource-count" className="text-sm font-medium">Resource Count</Label>
                           <Input
                             id="resource-count"
                             type="number"
                             value={resourceCount}
                             onChange={(e) => setResourceCount(Number(e.target.value))}
                             placeholder="Number of resources"
                             min="1"
                             className="mt-1"
                           />
                           <p className="text-xs text-muted-foreground mt-1">
                             Total cost will be multiplied by resource count
                           </p>
                         </div>
                       </div>
                     </div>

                     {/* Month Selection Grid */}
                     <div className="border rounded-lg overflow-hidden">
                       <div className="bg-muted/30 px-4 py-3 border-b">
                         <div className="flex items-center justify-between">
                           <h5 className="font-medium text-sm">Select Months</h5>
                           <span className="text-xs text-muted-foreground">
                             {monthSelections.filter(Boolean).length} of {planningMonths} selected
                           </span>
                         </div>
                       </div>
                       <div className="p-4">
                         <div className="grid grid-cols-6 md:grid-cols-12 gap-3">
                           {Array.from({ length: planningMonths }, (_, monthIndex) => (
                             <Button
                               key={monthIndex}
                               variant={monthSelections[monthIndex] ? "default" : "outline"}
                               size="sm"
                               onClick={() => {
                                 const newSelections = [...monthSelections];
                                 newSelections[monthIndex] = !newSelections[monthIndex];
                                 setMonthSelections(newSelections);
                               }}
                               className="h-10 w-full text-sm font-medium transition-all hover:scale-105"
                             >
                               {monthIndex + 1}
                             </Button>
                           ))}
                         </div>
                       </div>
                     </div>

                     {/* Quick Select Options */}
                     <div className="flex flex-wrap gap-2">
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => setMonthSelections(Array(planningMonths).fill(true))}
                       >
                         All Months
                       </Button>
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => setMonthSelections(Array(planningMonths).fill(false))}
                       >
                         Clear All
                       </Button>
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => {
                           const selections = Array(planningMonths).fill(false);
                           for (let i = 0; i < Math.min(12, planningMonths); i++) {
                             selections[i] = true;
                           }
                           setMonthSelections(selections);
                         }}
                       >
                         Year 1
                       </Button>
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => {
                           const selections = Array(planningMonths).fill(false);
                           for (let i = 0; i < planningMonths; i += 3) {
                             selections[i] = true;
                           }
                           setMonthSelections(selections);
                         }}
                       >
                         Quarterly
                       </Button>
                       <Button
                         variant="outline"
                         size="sm"
                         onClick={() => {
                           const selections = Array(planningMonths).fill(false);
                           for (let i = 0; i < planningMonths; i += 6) {
                             selections[i] = true;
                           }
                           setMonthSelections(selections);
                         }}
                       >
                         Bi-annually
                       </Button>
                     </div>
                  </div>
                )}

                 <DialogFooter>
                   <Button variant="outline" onClick={() => setShowMonthSelectionModal(false)}>
                     Cancel
                   </Button>
                   <Button
                     onClick={confirmAddCostWithMonths}
                     disabled={monthSelections.filter(Boolean).length === 0 || customAmount <= 0}
                   >
                     Add Cost ({monthSelections.filter(Boolean).length} months - ₹{(customAmount * resourceCount * monthSelections.filter(Boolean).length).toLocaleString()})
                   </Button>
                 </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button variant="outline" onClick={exportCosts}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
                <div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={importCosts}
                    style={{ display: 'none' }}
                    id="import-costs"
                  />
                  <Button variant="outline" onClick={() => document.getElementById('import-costs')?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </Button>
                </div>
              </div>

              {/* Catalog Results */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCatalog.map((template) => (
                  <Card key={template.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{template.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {template.description}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => addCostFromCatalog(template)}
                          className="ml-2 h-8 w-8 p-0"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {template.category.replace('-', ' ')}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {template.frequency}
                        </Badge>
                      </div>
                      {template.typicalRange && (
                        <div className="text-xs text-muted-foreground">
                          Typical: ₹{template.typicalRange.min.toLocaleString()} - ₹{template.typicalRange.max.toLocaleString()}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredCatalog.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No costs found matching your search.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="costs" className="space-y-6">
          {/* Operating Costs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle className="w-5 h-5 text-muted-foreground" />
                Your Operating Costs ({operatingCosts.length})
              </CardTitle>
              <CardDescription>
                Manage your selected operating costs and adjust monthly amounts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {operatingCosts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No operating costs added yet.</p>
                  <p className="text-sm">Use the catalog above to add standard costs or create custom ones.</p>
                </div>
              ) : (
                 <OperatingCostsTable
                   operatingCosts={operatingCosts}
                   planningMonths={planningMonths}
                   onUpdateCost={updateCost}
                   onRemoveCost={removeCost}
                   onEditCost={startEditingCost}
                 />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}